import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { Div } from 'client/widget-components/basicComponents';
import Text, { TextDomTag } from 'client/widget-components/basicComponents/Text';
import { ResponsiveStylesProp } from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { useMemo } from 'react';
import ColorSwatch from '../ColorSwatch';
import { ColorSwatchStyles } from '../ColorSwatch';

export interface ColorSwatchGroupStyles extends ColorSwatchStyles {
    title?: ResponsiveStylesProp;
    wrapper?: ResponsiveStylesProp;
    inputWrapper?: ResponsiveStylesProp;
}

export interface ColorOption {
    label?: string;
    value: string | number;
    color?: string;
    disabled?: boolean;
}

export interface ColorSwatchGroupProps {
    title?: string;
    options: ColorOption[];
    selectedValue?: ColorOption | Array<ColorOption>;
    onChange: (value: ColorOption) => void;
    styles?: ColorSwatchGroupStyles;
    size?: number;
    showLabels?: boolean;
}

export const ColorSwatchGroup = ({
    title,
    options,
    selectedValue,
    onChange,
    styles,
    size = 32,
    showLabels = false,
}: ColorSwatchGroupProps) => {

    const selectedValues = useMemo(() => {
        return selectedValue && new Set<string | number>(
            Array.isArray(selectedValue) ? selectedValue.map(({ value }) => value) : [selectedValue?.value]
        );
    }, [selectedValue]);

    const handleChange = (value: string | number) => {
        const selectedOption = options.find((opt) => opt.value === value);
        if (selectedOption) {
            onChange(selectedOption);
        }
    };

    return (
        <Div data-auto='color-swatch-group'>
            {title && (
                <Text
                    tag={TextDomTag.h5}
                    styles={[titleDefaultStyles, styles?.title]}
                    data-grab='colorswatchgroup-title'
                >
                    {title}
                </Text>
            )}
            <Div
                styles={[
                    wrapperDefaultStyles,
                    styles?.wrapper,
                ]}
                data-grab='colorswatchgroup-fields'
                role='radiogroup'
                aria-label={title}
            >
                {options.map((option) => (
                    <ColorSwatch
                        key={option.value}
                        color={option.color || '#000000'}
                        value={option.value}
                        selected={selectedValues?.has(option.value) || false}
                        disabled={option.disabled}
                        onChange={handleChange}
                        size={size}
                        styles={{
                            swatch: styles?.swatch,
                            swatchSelected: styles?.swatchSelected,
                        }}
                        showLabel={showLabels}
                    />
                ))}
            </Div>
        </Div>
    );
};

const wrapperDefaultStyles: ResponsiveStylesProp = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        flexWrap: 'wrap',
    },
};

const titleDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        textAlign: 'start',
        marginBlockEnd: '6px',
        lineHeight: '100%',
        marginTop: '0',
        fontSize: '16px',
        direction: 'inherit',
    },
};
